import type { InferResponseType } from "hono/client";
import useSWR from "swr";
import { apiClient } from "../rpc";

const meOrderByCode = apiClient.me.orders[":code"];

export type OrderDetail = Extract<
  InferResponseType<typeof meOrderByCode.$get>,
  { ok: true }
>["order"];

/** 404 (存在しない / 所有 email 不一致) を通常のエラーと区別するための番兵。 */
class OrderNotFoundError extends Error {}

const fetchOrder = async (code: string): Promise<OrderDetail> => {
  const res = await meOrderByCode.$get({ param: { code } });
  if (res.status === 200) {
    const data = await res.json();
    return data.order;
  }
  if (res.status === 404) throw new OrderNotFoundError("not-found");
  throw new Error(`HTTP ${res.status}`);
};

type State =
  | { status: "loading" }
  | { status: "ready"; order: OrderDetail }
  | { status: "not-found" }
  | { status: "error"; message: string };

/**
 * 注文詳細を取得する。SWR でキャッシュするため、一度見た注文に戻ってきたときは直前の内容を
 * そのまま表示したまま裏で再取得する (毎回ローディングに切り替わらない)。エラー時は自動リトライ
 * せず、UI 側の「再試行」ボタン (refetch) に委ねる。
 */
export const useOrder = (code: string): State & { refetch: () => Promise<void> } => {
  const { data, error, isLoading, mutate } = useSWR<OrderDetail, Error>(
    ["mypage:me/order", code],
    () => fetchOrder(code),
    { shouldRetryOnError: false },
  );

  const refetch = async () => {
    await mutate();
  };

  // data があれば (再取得中でも) 直前の内容を出し続ける。無い場合だけ 404 / エラー / ローディング。
  if (data !== undefined) return { status: "ready", order: data, refetch };
  if (error instanceof OrderNotFoundError) return { status: "not-found", refetch };
  if (error) return { status: "error", message: error.message, refetch };
  if (isLoading) return { status: "loading", refetch };
  return { status: "loading", refetch };
};
